/**
 * useNowSeconds — live Unix-second clock with periodic re-render (#1693)
 *
 * Returns the current Unix-seconds timestamp and re-renders the caller on a
 * fixed interval (default 60 s). The initial value is captured at mount via
 * `useState(() => Math.floor(Date.now() / 1000))`, which is the React 19
 * render-purity pattern established by #1689 / #1685 (impure reads of
 * `Date.now()` must not happen during render). Subsequent updates happen
 * inside a `setInterval` callback — outside the render phase — so the
 * `react-hooks` / React Compiler purity rules stay satisfied.
 *
 * Why this exists: #1689's lint fix froze `now` at component mount, which
 * regressed "isPast" badging on long-lived list views (events past their
 * start time still rendered as upcoming until route change / hard refresh).
 * Three call sites needed the same fix; this hook is the single source of
 * truth so they all tick together.
 *
 * Picking an interval:
 *   - 60_000 ms (default) matches the granularity of every consumer today:
 *     upcoming/past splits and category labels in the room switcher don't
 *     change faster than per-minute, and a 60 s tick is well under the
 *     POST_EVENT_VISIBILITY_BUFFER_SECONDS (1 h) window. The render cost is
 *     a handful of list rows once a minute.
 *   - Override for surfaces with sub-minute requirements (timers, countdowns)
 *     — but expect coordinated re-renders across all consumers in that tree.
 *
 * Not for: data fetching (use TanStack Query), countdown displays needing
 * sub-second precision (use requestAnimationFrame), or anything where the
 * "now" reference must be exactly synchronized with a network event (use
 * a server timestamp instead).
 */
import { useEffect, useState } from 'react';

export function useNowSeconds(intervalMs: number = 60_000): number {
  const [now, setNow] = useState<number>(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const id = setInterval(() => {
      setNow(Math.floor(Date.now() / 1000));
    }, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  return now;
}
